import { AxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../components/AsyncTable';
import NewRegisterBtn from '../../../components/Buttons/NewRegisterBtn';
import {
  ActionButtonsContainer,
  MdFileCopyIcon,
  ViewIconTable,
} from '../../../components/TablePaginator/styles';
import { backendErrorNotification } from '../../../components/Notification';
import HttpStatus from '../../../enums/httpStatus';
import { messageApi } from '../../../services/requests';
import { Message } from '../../../types/apiResponse/message';
import ContentContainer from '../../../templates/Content/ContentContainer';
import CreateMessageModal from './components/CreateMessageModal';
import MessageDetailsModal from './components/MessageDetailsModal';

type MessageDraft = {
  subject?: string;
  message?: string;
};

const MessagesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [messages, setMessages] = useState<Message[]>([]);
  const [tableReloadFlag, setTableReloadFlag] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
  const [draftMessage, setDraftMessage] = useState<MessageDraft | undefined>();

  const handleRequestMessages: RequestPage = useCallback(
    async ({ search, page, quantityPerPage }) => {
      let hasMore = true;

      try {
        messageApi.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
        const response = await messageApi.index();

        if (response.status !== HttpStatus.OK) {
          throw response;
        }

        const {
          data,
          paginated,
        }: {
          data: Message[];
          paginated?: { totalPage?: number } | null;
        } = response.data;

        if (!data || data.length === 0) {
          hasMore = false;
        }

        return {
          data: data ?? [],
          hasMore,
          totalPage: paginated?.totalPage ?? 0,
        };
      } catch (error) {
        if (!messageApi.axios.isCancel(error)) {
          backendErrorNotification(error as AxiosError);
        }

        return {
          data: [],
          hasMore: false,
          totalPage: 0,
        };
      } finally {
        messageApi.query = '';
      }
    },
    [],
  );

  const handleOpenCreateModal = useCallback(() => {
    setDraftMessage(undefined);
    setShowCreateModal(true);
  }, []);

  const handleCopyMessage = useCallback((message: Message) => {
    setDraftMessage({
      subject: message.Assunto,
      message: message.Mensagem,
    });
    setShowCreateModal(true);
  }, []);

  const handleOpenDetails = useCallback((message: Message) => {
    setSelectedMessageId(message.CodigoMensagem);
    setShowDetailsModal(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setDraftMessage(undefined);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedMessageId(null);
  }, []);

  const handleRefreshTable = useCallback(() => {
    setTableReloadFlag((value) => value + 1);
  }, []);

  const renderActions = useCallback(
    (message: Message) => (
      <ActionButtonsContainer>
        <ViewIconTable title={t('viewDetails')} onClick={() => handleOpenDetails(message)} />
        <MdFileCopyIcon title={t('copy')} onClick={() => handleCopyMessage(message)} />
      </ActionButtonsContainer>
    ),
    [handleCopyMessage, handleOpenDetails, t],
  );

  const renderCreatedAt = useCallback(
    (message: Message) => {
      if (!message.DataCadastro) {
        return '-';
      }

      if (!message.HoraCadastro) {
        return message.DataCadastro;
      }

      return `${message.DataCadastro} ${message.HoraCadastro}`;
    },
    [],
  );

  const columns: Column<Message>[] = useMemo(
    () => [
      {
        label: t('code') as string,
        accessor: 'CodigoMensagem',
      },
      {
        label: t('subject') as string,
        accessor: 'Assunto',
      },
      {
        label: t('sentTo') as string,
        Cell: (row: Message) => String(row.recipients_who_received_count ?? 0),
      },
      {
        label: t('read') as string,
        Cell: (row: Message) => String(row.recipients_who_read_count ?? 0),
      },
      {
        label: t('unread') as string,
        Cell: (row: Message) => {
          const received = row.recipients_who_received_count ?? 0;
          const read = row.recipients_who_read_count ?? 0;
          return String(received - read);
        },
      },
      {
        label: t('createdAt') as string,
        Cell: (row: Message) => renderCreatedAt(row),
      },
      {
        label: '',
        Cell: (row: Message) => renderActions(row),
      },
    ],
    [renderActions, renderCreatedAt, t],
  );

  return (
    <ContentContainer title={t('messages')}>
      <NewRegisterBtn
        onClick={handleOpenCreateModal}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {showCreateModal && (
        <CreateMessageModal
          open={showCreateModal}
          onClose={handleCloseCreateModal}
          onSuccess={handleRefreshTable}
          initialData={draftMessage}
        />
      )}

      {showDetailsModal && (
        <MessageDetailsModal
          open={showDetailsModal}
          onClose={handleCloseDetailsModal}
          messageId={selectedMessageId}
        />
      )}

      <AsyncTable
        tableName="messages"
        columns={columns}
        value={messages}
        onChange={setMessages}
        requestPage={handleRequestMessages}
        reqListener={[tableReloadFlag]}
        options={{
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          styles: {
            primaryColor: theme.colors.surface,
            secondaryColor: theme.colors.onSurface,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
        }}
      />
    </ContentContainer>
  );
};

export default MessagesPage;
